// Phase 4-prep sub-task 4-prep.6 — `resolveColumnRef`. Splits the
// ColumnRef-related runtime helpers out of `value.ts` to avoid pulling
// `normalize.ts`/`Session` imports into the bottom of the dependency
// graph. The ColumnRef class itself stays in `value.ts` next to the
// rest of the `Value` union.

import type { SourcePos } from './ast.ts';
import { UnboundSymbolError, UnknownColumnRefError } from './errors.ts';
import { type AstInput, astShape } from './normalize.ts';
import { schemaFromEntries } from './schema/types.ts';
import type { Session } from './session/Session.ts';
import {
  type Cell,
  type ColumnRef,
  type Dataset,
  expectDataset,
  expectLazy,
  isMaterialized,
  LazyDataset,
  MaterializedDataset,
} from './value.ts';

/**
 * Resolve a `ColumnRef` to a single-column `Dataset` (PHASE_4_PREP.md
 * D9, sub-task 4-prep.6). Three steps:
 *
 *   1. **Resolve `ref.ds`.**
 *      - `Dataset` instance (Form 2 — captured at construction time)
 *        → use directly.
 *      - `string` (Form 1 — binding name) → look up via
 *        `session.getBinding`. Miss → `UnboundSymbolError`. Non-
 *        Dataset binding → `TypeMismatchError` (via `expectDataset`).
 *
 *      Env lookup is intentionally NOT performed here: by the time a
 *      consumer (dashboard renderer, materializer, future qkg `in`
 *      clause) calls `resolveColumnRef`, the lexical Env that bound
 *      the name during the original evaluation is out of scope.
 *      Session bindings persist across that boundary, which is the
 *      load-bearing case. The col op's apply path runs Env → Session
 *      at construction time and captures the symbolic name on the
 *      ref, so the binding is still discoverable at resolution time
 *      (provided the caller also did `session.recordBinding`, which is
 *      the chat-tool convention).
 *
 *   2. **Validate the column exists** on the source schema → missing
 *      → `UnknownColumnRefError` with the available column list as
 *      the suggestion set.
 *
 *   3. **Project to a single-column Dataset.**
 *      - `MaterializedDataset` → new `MaterializedDataset` carrying
 *        only `[ref.column]` with the matching schema entry.
 *      - `LazyDataset` → new `LazyDataset` with a `select` plan node
 *        appended to the existing chain. The plan node carries the
 *        single-column `schemaAfter` so plan replay sees the right
 *        shape.
 *
 * `ref.path` is intentionally NOT acted on here. Paths walk into
 * struct-cell values, which is per-cell logic of the *consumer* (the
 * dashboard tile / qkg `in` clause that downstream-projects the leaf
 * value out of each cell). The col op's apply validates the path
 * against the schema at construction time, so a ref reaching
 * `resolveColumnRef` already has a schema-valid path; the path field
 * is preserved on the ref for the consumer to read.
 *
 * `pos` is the source-position threaded into any error raised by this
 * helper. Callers that resolve refs outside an expression context
 * (e.g., dashboard render time) pass `NO_POS`.
 */
export const resolveColumnRef = (ref: ColumnRef, session: Session, pos: SourcePos): Dataset => {
  // Single-line description for error context — references the
  // symbolic name for Form 1, or the literal token `<dataset>` for
  // Form 2 since there's no name to show.
  const context = `(col ${typeof ref.ds === 'string' ? ref.ds : '<dataset>'} ${ref.column})`;

  // Step 1: resolve `ds` → Dataset.
  const dataset = resolveRefDataset(ref, session, context, pos);

  // Step 2: validate the column exists on the schema.
  const colType = dataset.schema.get(ref.column);
  if (colType === undefined) {
    throw new UnknownColumnRefError(ref.column, Array.from(dataset.schema.keys()), pos);
  }

  // Step 3: project to single-column Dataset.
  const newSchema = schemaFromEntries([[ref.column, colType]]);
  if (isMaterialized(dataset)) {
    return new MaterializedDataset(
      // The MaterializedDataset constructor enforces schema-keys-match-
      // columns; we know `ref.column` is in the schema (step 2) so the
      // column data IS present. The `?? []` fallback is here only to
      // satisfy `noUncheckedIndexedAccess`; the branch is unreachable.
      { [ref.column]: dataset.columns[ref.column] ?? [] },
      newSchema,
    );
  }
  // Lazy projection: append a `select` plan node carrying the single-
  // column schemaAfter. The plan-replay path executes this via the
  // standard `select` verb at materialisation time. `expectLazy`
  // narrows the abstract `Dataset` (we already returned in the
  // materialized branch) to `LazyDataset` for `.plan` access.
  const lazy = expectLazy(dataset, context, pos);
  const selectCall: AstInput = { name: 'select', params: [[{ $sym: ref.column }]] };
  return new LazyDataset(
    {
      source: lazy.plan.source,
      ops: [...lazy.plan.ops, { verb: 'select', args: selectCall, schemaAfter: newSchema }],
    },
    newSchema,
  );
};
/**
 * Walk a struct sub-path into a single cell, yielding the leaf
 * scalar (`null` short-circuits to `null`). The SSOT path-walk
 * kernel shared by {@link projectColumnRef} and the `join` verb's
 * struct-sub-path key projection — keep both on this one
 * implementation so the null/short-circuit semantics never diverge.
 */
export const walkStructPath = (cell: unknown, path: readonly string[]): unknown =>
  path.reduce<unknown>((v, seg) => (v == null ? null : (v as Record<string, unknown>)[seg]), cell);
/**
 * Resolve a `(col …)` / join-`:on` label list against a set of
 * available column names. The first label that names a column is the
 * column; any labels before it are an ignored (binding-name) prefix;
 * labels after it form the struct sub-path. `undefined` when no label
 * names a known column — callers decide the policy (the join verb
 * throws a targeted error; the `(col …)` op's expression-context
 * inference falls back to `unknown`, and its per-row compile applies
 * a positional fallback).
 *
 * SSOT for the "which label is the column?" rule so the mental model
 * never diverges by context (join `:on` vs. `derive`/`filter`
 * expression bodies): the binding name is always optional and
 * irrelevant — the role / current dataset decides the source.
 */
export const pickColumnPath = (
  labels: readonly string[],
  hasColumn: (name: string) => boolean,
): { readonly column: string; readonly path: readonly string[] } | undefined => {
  const i = labels.findIndex(hasColumn);
  if (i === -1) {
    return undefined;
  }
  return { column: labels[i] as string, path: labels.slice(i + 1) };
};
/**
 * Tolerant `(col …)` label extractor: the symbol / string-literal
 * labels of a `(col …)` opCall node, or `undefined` when `node` is
 * not a `(col …)` call or any label is a non-literal (e.g. a Form-2
 * `(col (expr) …)` first arg). SSOT for "the name labels of a `(col
 * …)`" — shared by the `(col …)` expression-context compiler and the
 * predicate set-ref / per-row discriminator so the parse never
 * diverges by context. Strict callers (`(col …)` apply, join `:on`)
 * keep their own context-tailored error messages.
 */
export const colNodeLabels = (node: unknown): readonly string[] | undefined => {
  const s = astShape(node as AstInput);
  if (s.kind !== 'opCall' || s.call.name !== 'col') {
    return undefined;
  }
  const out: string[] = [];
  for (const p of s.call.params ?? []) {
    const ps = astShape(p as AstInput);
    if (ps.kind === 'symbolRef') {
      out.push(ps.sym);
    } else if (ps.kind === 'primitive' && typeof ps.value === 'string') {
      out.push(ps.value);
    } else {
      return undefined;
    }
  }
  return out;
};

/**
 * Resolve a `ColumnRef` to its per-cell, path-walked scalar cells —
 * the single source of truth for struct-path projection.
 *
 * Combines the three steps every consumer of a `ColumnRef` needs:
 *
 *   1. Project the ref to a single-column `Dataset` via
 *      {@link resolveColumnRef} (Form-1 session lookup / Form-2 inline
 *      dataset + column-existence validation).
 *   2. Materialise that projection if it is lazy (`materialize`
 *      callback — pre-bound by the caller so this helper doesn't take
 *      an `EvalContext`; the predicate path passes `deps.materialize`,
 *      the React path passes the bound `useMaterialize`).
 *   3. Walk `ref.path` into each cell, yielding the leaf scalar value
 *      (the exact reduce kernel previously inlined in
 *      `resolveSetRefsInPredicate`). `ref.path` is already
 *      schema-validated at `col` apply time, so the walk is plain,
 *      safe struct access — `null` short-circuits to `null`.
 *
 * Returns the column name (`ref.column`) plus the path-walked cell
 * array so callers that re-attach the projection as a synthetic
 * column (the `join :on` `(col …)` path) get the name without
 * re-deriving it.
 */
export const projectColumnRef = async (
  ref: ColumnRef,
  session: Session,
  pos: SourcePos,
  materialize: (ds: LazyDataset) => Promise<MaterializedDataset>,
): Promise<{ readonly column: string; readonly cells: readonly Cell[] }> => {
  const projected = resolveColumnRef(ref, session, pos);
  const mat = isMaterialized(projected)
    ? projected
    : await materialize(expectLazy(projected, `(col … ${ref.column})`, pos));
  const cells: readonly Cell[] = mat.columns[ref.column] ?? [];
  const walked = cells.map((cell) => walkStructPath(cell, ref.path));
  return { column: ref.column, cells: walked as readonly Cell[] };
};

/**
 * Resolve a session binding *name* to a Dataset value (the Form-1
 * path). SSOT for "named binding → Dataset": an unbound name throws
 * `UnboundSymbolError` with the pooled binding-name suggestions
 * (engine `UnknownNameError` convention — gives the agent a "did you
 * mean…" list); a bound-but-non-Dataset value throws `TypeMismatchError`
 * via `expectDataset`. Consumed by `resolveRefDataset` (Form-1 branch)
 * and by the `join` verb's positional-0 resolution — neither
 * re-implements the getBinding/throw/narrow ceremony.
 */
export const resolveBindingDataset = (session: Session, name: string, context: string, pos: SourcePos): Dataset => {
  const value = session.getBinding(name);
  if (value === undefined) {
    throw new UnboundSymbolError(name, session.bindingNames(), pos);
  }
  return expectDataset(value, context, pos);
};
/**
 * Step-1 helper for `resolveColumnRef`. Resolves the `ds` half of a
 * ColumnRef to a Dataset value — either using the inlined Dataset
 * directly (Form 2) or resolving the binding name in the session
 * (Form 1) via the shared `resolveBindingDataset` SSOT. Factored out
 * to keep the main entry's three-step body scannable; not exported
 * (the public API is `resolveColumnRef`).
 */
const resolveRefDataset = (ref: ColumnRef, session: Session, context: string, pos: SourcePos): Dataset => {
  if (typeof ref.ds !== 'string') {
    return ref.ds;
  }
  return resolveBindingDataset(session, ref.ds, context, pos);
};
